/**
 * Gimnasio o casa (Req. 10.9; 0.3.4).
 *
 * - Cada modalidad tiene sus rutinas y su progresión: la vez pasada de un
 *   ejercicio sale de entrenamientos de la misma modalidad.
 * - Las rutinas y los entrenamientos anteriores a la 0.3.4 no traen modalidad
 *   y se leen como gimnasio, sin reescribirlos.
 * - Elegir la modalidad en Gym solo cambia lo que se muestra: no borra, no
 *   reemplaza y no duplica nada.
 * - En casa se puede registrar, por ejercicio, la pausa en cada repetición y
 *   la variante con que se hizo.
 */

import type { Modalidad } from '@/db/types';
import { ErrorValidacion } from './errors';

export const MODALIDADES: readonly Modalidad[] = ['gimnasio', 'casa'];

export const ETIQUETAS_MODALIDAD: Readonly<Record<Modalidad, string>> = {
  gimnasio: 'Gimnasio',
  casa: 'Casa',
};

/** La modalidad de una rutina o un entrenamiento; sin dato (o un dato raro), gimnasio. */
export function modalidadDe(x: { modalidad?: unknown } | null | undefined): Modalidad {
  return x?.modalidad === 'casa' ? 'casa' : 'gimnasio';
}

export const PAUSA_MAXIMA_SEG = 120;
export const VARIANTE_MAXIMA = 60;

export interface PausaYVariante {
  pausaSeg?: number | null;
  variante?: string | null;
}

/** Valida y limpia: pausa entera de 0 a 120 s (0 = sin pausa) y variante de hasta 60 letras. */
export function limpiarPausaYVariante(x: PausaYVariante): { pausaSeg: number | null; variante: string | null } {
  const pausa = x.pausaSeg ?? null;
  if (pausa !== null && !(Number.isInteger(pausa) && pausa >= 0 && pausa <= PAUSA_MAXIMA_SEG)) {
    throw new ErrorValidacion(`La pausa debe ser de 0 a ${PAUSA_MAXIMA_SEG} segundos, en números enteros.`, 'pausa');
  }
  const variante = (x.variante ?? '').trim().replace(/\s+/g, ' ');
  if (variante.length > VARIANTE_MAXIMA) {
    throw new ErrorValidacion(`La variante no puede pasar de ${VARIANTE_MAXIMA} caracteres.`, 'variante');
  }
  return { pausaSeg: pausa === 0 ? null : pausa, variante: variante === '' ? null : variante };
}

/** "variante: inclinadas · pausa 2 s", o '' si no hay ninguna. */
export function textoPausaYVariante(x: PausaYVariante): string {
  const partes = [
    x.variante === null || x.variante === undefined || x.variante.trim() === '' ? null : `variante: ${x.variante.trim()}`,
    x.pausaSeg === null || x.pausaSeg === undefined || x.pausaSeg <= 0 ? null : `pausa ${x.pausaSeg} s`,
  ];
  return partes.filter((p): p is string => p !== null).join(' · ');
}
